import React from "react";
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Trash2,
  UserCheck,
  Users,
} from "lucide-react";
import type {
  SspPartyResponse,
  SspResponsiblePartyResponse,
  SspRoleResponse,
} from "../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from "../../../components/Modal";
import { Dropdown } from "../../../components/Dropdown";
import { Tip } from "../../../components/ui/tooltip";
import { LoadingSpinner } from "../components/LoadingSpinner";

export interface RolesPartiesTabProps {
  rolesPartiesLoading: boolean;
  isPublishedSsp: boolean;
  requestDelete: (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => void;

  // Roles
  roles: SspRoleResponse[];
  rolesPagination: { page: number; pageSize: number };
  setRolesPagination: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  editingRoleId: string | null;
  setEditingRoleId: React.Dispatch<React.SetStateAction<string | null>>;
  editRoleForm: {
    roleId: string;
    title: string;
    shortName: string;
    description: string;
  };
  setEditRoleForm: React.Dispatch<
    React.SetStateAction<{
      roleId: string;
      title: string;
      shortName: string;
      description: string;
    }>
  >;
  savingRole: boolean;
  handleUpdateRole: () => void;
  deletingRoleId: string | null;
  handleDeleteRole: (roleDbId: string) => Promise<void>;
  setShowAddRole: React.Dispatch<React.SetStateAction<boolean>>;

  // Parties
  parties: SspPartyResponse[];
  partiesPagination: { page: number; pageSize: number };
  setPartiesPagination: React.Dispatch<
    React.SetStateAction<{ page: number; pageSize: number }>
  >;
  editingPartyId: string | null;
  setEditingPartyId: React.Dispatch<React.SetStateAction<string | null>>;
  editPartyForm: {
    type: "person" | "organization";
    name: string;
    shortName: string;
    email: string;
  };
  setEditPartyForm: React.Dispatch<
    React.SetStateAction<{
      type: "person" | "organization";
      name: string;
      shortName: string;
      email: string;
    }>
  >;
  savingParty: boolean;
  handleUpdateParty: () => void;
  deletingPartyId: string | null;
  handleDeleteParty: (partyDbId: string) => Promise<void>;
  setShowAddParty: React.Dispatch<React.SetStateAction<boolean>>;

  // Responsible Parties
  responsibleParties: SspResponsiblePartyResponse[];
  deletingRPId: string | null;
  handleDeleteRP: (rpId: string) => Promise<void>;
  setShowAssignRP: React.Dispatch<React.SetStateAction<boolean>>;
}

export function RolesPartiesTab({
  rolesPartiesLoading,
  isPublishedSsp,
  requestDelete,
  roles,
  rolesPagination,
  setRolesPagination,
  editingRoleId,
  setEditingRoleId,
  editRoleForm,
  setEditRoleForm,
  savingRole,
  handleUpdateRole,
  deletingRoleId,
  handleDeleteRole,
  setShowAddRole,
  parties,
  partiesPagination,
  setPartiesPagination,
  editingPartyId,
  setEditingPartyId,
  editPartyForm,
  setEditPartyForm,
  savingParty,
  handleUpdateParty,
  deletingPartyId,
  handleDeleteParty,
  setShowAddParty,
  responsibleParties,
  deletingRPId,
  handleDeleteRP,
  setShowAssignRP,
}: RolesPartiesTabProps) {
  return (
    <>
      {rolesPartiesLoading ? (
        <LoadingSpinner text="Loading roles & parties" />
      ) : (
        <div className="space-y-6">
          {/* Roles Section */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <span className="flex items-center justify-center size-8 rounded-lg bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 ring-1 ring-violet-100 dark:ring-violet-500/20">
                  <UserCheck className="size-4" />
                </span>
                <span className="text-foreground">Roles</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-100 dark:border-violet-500/20">
                  {roles.length}
                </span>
              </h3>
              {!isPublishedSsp && (
                <button
                  onClick={() => setShowAddRole(true)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="size-3" /> Add Role
                </button>
              )}
            </div>
            {roles.length > 0 ? (
              <div className="space-y-4">
                {/* Pagination controls for roles */}
                {roles.length > 10 && (
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <label className="text-xs dark:text-white text-foreground whitespace-nowrap">Per page</label>
                      <Dropdown
                        value={String(rolesPagination.pageSize)}
                        onChange={(v) => setRolesPagination({ page: 1, pageSize: Number(v) })}
                        options={[5, 10, 25, 50].map((n) => ({
                          value: String(n),
                          label: String(n),
                        }))}
                        size="sm"
                        align="start"
                        className="!min-w-[70px]"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {Math.min((rolesPagination.page - 1) * rolesPagination.pageSize + 1, roles.length)}–{Math.min(rolesPagination.page * rolesPagination.pageSize, roles.length)} of {roles.length}
                    </p>
                  </div>
                )}

                {/* Roles list */}
                <div className="divide-y divide-border">
                  {roles
                    .slice((rolesPagination.page - 1) * rolesPagination.pageSize, rolesPagination.page * rolesPagination.pageSize)
                    .map((role) => (
                      <div key={role.id} className="py-2.5">
                        {editingRoleId === role.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <FormField label="Title" required>
                                <FormInput
                                  value={editRoleForm.title}
                                  onChange={(v) =>
                                    setEditRoleForm((f) => ({ ...f, title: v }))
                                  }
                                  placeholder="Role title"
                                />
                              </FormField>
                              <FormField label="Short Name">
                                <FormInput
                                  value={editRoleForm.shortName}
                                  onChange={(v) =>
                                    setEditRoleForm((f) => ({
                                      ...f,
                                      shortName: v,
                                    }))
                                  }
                                  placeholder="Short name"
                                />
                              </FormField>
                            </div>
                            <FormField label="Description">
                              <FormTextarea
                                value={editRoleForm.description}
                                onChange={(v) =>
                                  setEditRoleForm((f) => ({
                                    ...f,
                                    description: v,
                                  }))
                                }
                                placeholder="Description"
                              />
                            </FormField>
                            <div className="flex items-center gap-2 pt-1">
                              <button
                                onClick={() => setEditingRoleId(null)}
                                className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleUpdateRole}
                                disabled={!editRoleForm.title || savingRole}
                                className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                              >
                                {savingRole ? "Saving" : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center size-9 rounded-lg bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5">
                              <UserCheck className="size-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                {role.title}
                              </p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="inline-flex items-center text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-100 dark:border-violet-500/20">
                                  {role.roleId}
                                </span>
                                {role.shortName && (
                                  <span className="text-xs text-muted-foreground">
                                    {role.shortName}
                                  </span>
                                )}
                              </div>
                              {role.description && (
                                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                  {role.description}
                                </p>
                              )}
                            </div>
                            <div
                              className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                            >
                              <Tip label="Edit Role" side="bottom">
                                <button
                                  onClick={() => {
                                    setEditingRoleId(role.id);
                                    setEditRoleForm({
                                      roleId: role.roleId,
                                      title: role.title,
                                      shortName: role.shortName || "",
                                      description: role.description || "",
                                    });
                                  }}
                                  className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                                >
                                  <Pencil className="size-3.5" />
                                </button>
                              </Tip>
                              <Tip label="Delete Role" side="bottom">
                                <button
                                  onClick={() =>
                                    requestDelete(
                                      "Role",
                                      role.id,
                                      role.title,
                                      () => handleDeleteRole(role.id),
                                    )
                                  }
                                  disabled={deletingRoleId === role.id}
                                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </Tip>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {/* Pagination buttons for roles */}
                {Math.ceil(roles.length / rolesPagination.pageSize) > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-3 border-t border-border">
                    <button
                      onClick={() => setRolesPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={rolesPagination.page === 1}
                      className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="size-3.5" />
                    </button>
                    <span className="text-xs text-muted-foreground">
                      Page {rolesPagination.page} of {Math.ceil(roles.length / rolesPagination.pageSize)}
                    </span>
                    <button
                      onClick={() => setRolesPagination((p) => ({ ...p, page: Math.min(Math.ceil(roles.length / rolesPagination.pageSize), p.page + 1) }))}
                      disabled={rolesPagination.page >= Math.ceil(roles.length / rolesPagination.pageSize)}
                      className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                No roles defined. Add roles like ISSO, System Owner, AO.
              </p>
            )}
          </div>

          {/* Parties Section */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <span className="flex items-center justify-center size-8 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-1 ring-sky-100 dark:ring-sky-500/20">
                  <Users className="size-4" />
                </span>
                <span className="text-foreground">Parties</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-100 dark:border-sky-500/20">
                  {parties.length}
                </span>
              </h3>
              {!isPublishedSsp && (
                <button
                  onClick={() => setShowAddParty(true)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="size-3" /> Add Party
                </button>
              )}
            </div>
            {parties.length > 0 ? (
              <div className="space-y-4">
                {/* Pagination controls for parties */}
                {parties.length > 10 && (
                  <div className="flex items-center justify-between pb-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <label className="text-xs dark:text-white text-foreground whitespace-nowrap">Per page</label>
                      <Dropdown
                        value={String(partiesPagination.pageSize)}
                        onChange={(v) => setPartiesPagination({ page: 1, pageSize: Number(v) })}
                        options={[5, 10, 25, 50].map((n) => ({
                          value: String(n),
                          label: String(n),
                        }))}
                        size="sm"
                        align="start"
                        className="!min-w-[70px]"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {Math.min((partiesPagination.page - 1) * partiesPagination.pageSize + 1, parties.length)}–{Math.min(partiesPagination.page * partiesPagination.pageSize, parties.length)} of {parties.length}
                    </p>
                  </div>
                )}

                {/* Parties list */}
                <div className="divide-y divide-border">
                  {parties
                    .slice((partiesPagination.page - 1) * partiesPagination.pageSize, partiesPagination.page * partiesPagination.pageSize)
                    .map((party) => (
                      <div key={party.id} className="py-2.5">
                        {editingPartyId === party.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <FormField label="Name" required>
                                <FormInput
                                  value={editPartyForm.name}
                                  onChange={(v) =>
                                    setEditPartyForm((f) => ({ ...f, name: v }))
                                  }
                                  placeholder="Party name"
                                />
                              </FormField>
                              <FormField label="Type">
                                <FormSelect
                                  options={[
                                    { label: "Person", value: "person" },
                                    {
                                      label: "Organization",
                                      value: "organization",
                                    },
                                  ]}
                                  value={editPartyForm.type}
                                  onChange={(v) =>
                                    setEditPartyForm((f) => ({
                                      ...f,
                                      type: v as "person" | "organization",
                                    }))
                                  }
                                  placeholder="Select type"
                                />
                              </FormField>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <FormField label="Short Name">
                                <FormInput
                                  value={editPartyForm.shortName}
                                  onChange={(v) =>
                                    setEditPartyForm((f) => ({
                                      ...f,
                                      shortName: v,
                                    }))
                                  }
                                  placeholder="Short name"
                                />
                              </FormField>
                              <FormField label="Email">
                                <FormInput
                                  value={editPartyForm.email}
                                  onChange={(v) =>
                                    setEditPartyForm((f) => ({
                                      ...f,
                                      email: v,
                                    }))
                                  }
                                  placeholder="Email address"
                                />
                              </FormField>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <button
                                onClick={() => setEditingPartyId(null)}
                                className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleUpdateParty}
                                disabled={!editPartyForm.name || savingParty}
                                className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                              >
                                {savingParty ? "Saving" : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3">
                            {party.partyType === "organization" ? (
                              <div className="size-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Building2 className="size-4 text-amber-600 dark:text-amber-400" />
                              </div>
                            ) : (
                              <div className="size-9 rounded-lg bg-sky-50 dark:bg-sky-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Users className="size-4 text-sky-600 dark:text-sky-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">
                                {party.name}
                              </p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${party.partyType === "organization"
                                  ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                  : "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20"
                                  }`}>
                                  {party.partyType}
                                </span>
                                {party.shortName && (
                                  <span className="text-xs text-muted-foreground">
                                    {party.shortName}
                                  </span>
                                )}
                                {party.emailAddresses.length > 0 && (
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <span className="size-1 rounded-full bg-muted-foreground/50" />
                                    {party.emailAddresses[0]}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                            >
                              <Tip label="Edit Party" side="bottom">
                                <button
                                  onClick={() => {
                                    setEditingPartyId(party.id);
                                    setEditPartyForm({
                                      type: party.partyType as
                                        | "person"
                                        | "organization",
                                      name: party.name,
                                      shortName: party.shortName || "",
                                      email: party.emailAddresses[0] || "",
                                    });
                                  }}
                                  className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 transition-colors"
                                >
                                  <Pencil className="size-3.5" />
                                </button>
                              </Tip>
                              <Tip label="Delete Party" side="bottom">
                                <button
                                  onClick={() =>
                                    requestDelete(
                                      "Party",
                                      party.id,
                                      party.name,
                                      () => handleDeleteParty(party.id),
                                    )
                                  }
                                  disabled={deletingPartyId === party.id}
                                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </Tip>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {/* Pagination buttons for parties */}
                {Math.ceil(parties.length / partiesPagination.pageSize) > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-3 border-t border-border">
                    <button
                      onClick={() => setPartiesPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={partiesPagination.page === 1}
                      className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="size-3.5" />
                    </button>
                    <span className="text-xs text-muted-foreground">
                      Page {partiesPagination.page} of {Math.ceil(parties.length / partiesPagination.pageSize)}
                    </span>
                    <button
                      onClick={() => setPartiesPagination((p) => ({ ...p, page: Math.min(Math.ceil(parties.length / partiesPagination.pageSize), p.page + 1) }))}
                      disabled={partiesPagination.page >= Math.ceil(parties.length / partiesPagination.pageSize)}
                      className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="size-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                No parties defined. Add people or organizations involved.
              </p>
            )}
          </div>

          {/* Responsible Parties Section */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <span className="flex items-center justify-center size-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-100 dark:ring-emerald-500/20">
                  <UserCheck className="size-4" />
                </span>
                <span className="text-foreground">Responsible Party Assignments</span>
              </h3>
              {!isPublishedSsp && (
                <button
                  onClick={() => setShowAssignRP(true)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="size-3" /> Assign
                </button>
              )}
            </div>
            {responsibleParties.length > 0 ? (
              <div className="divide-y divide-border">
                {responsibleParties.map((rp) => {
                  const role = roles.find((r) => r.roleId === rp.roleId);
                  return (
                    <div
                      key={rp.id}
                      className="flex items-start gap-3 py-3"
                    >
                      <div className="flex items-center justify-center size-9 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5">
                        <UserCheck className="size-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          {role?.title || rp.roleId}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {rp.partyUuids.map((uuid) => {
                            const party = parties.find(
                              (p) => p.partyUuid === uuid,
                            );
                            const isOrg = party?.partyType === "organization";
                            return (
                              <span
                                key={uuid}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isOrg
                                  ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                  : "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20"
                                  }`}
                              >
                                {isOrg ? (
                                  <Building2 className="size-2.5" />
                                ) : (
                                  <Users className="size-2.5" />
                                )}
                                {party?.name || uuid.slice(0, 12) + "…"}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div
                        className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                      >
                        <Tip label="Delete Responsible Party Assignment" side="bottom">
                          <button
                            onClick={() =>
                              requestDelete(
                                "Responsible Party Assignment",
                                rp.id,
                                role?.title || rp.roleId,
                                () => handleDeleteRP(rp.id),
                              )
                            }
                            disabled={deletingRPId === rp.id}
                            className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </Tip>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">
                No assignments yet. Map roles to parties.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
